import { evaluateConditions } from '@bmo/form-core';

import type { SelectOption, WorkflowField, WorkflowStep } from '@/api/workflows';

import type { FieldValue } from './FieldRenderer';

/**
 * A resolved "this answer belongs on a different form" rule — the field whose
 * option was picked, the option itself, and the destination the runtime should
 * offer. Exactly one of `templateId` / `url` is ever set: the domain refuses the
 * pair, and {@link findOptionRedirect} refuses to resolve an option carrying
 * both rather than silently picking a winner.
 */
export type OptionRedirect = {
  field: WorkflowField;
  option: SelectOption;
  /** Another workflow on this platform. The HOST page decides what that means —
   *  preview opens that workflow's preview, advisor runtime starts a fresh
   *  application on it — which is why this stays an id and never a URL. */
  templateId: string | null;
  /** Absolute http(s) destination outside the platform. */
  url: string | null;
};

/**
 * The redirect rule the advisor's CURRENT answers have triggered on this step,
 * or null.
 *
 * RadioGroup only, mirroring the domain: a single-pick control is the only one
 * where "you have answered your way off this form" is unambiguous.
 *
 * Visibility is respected the same way `findBlockingField` respects it — a field
 * or section hidden by the current values can't have been answered by the
 * advisor, and a stale value left behind a now-hidden condition must not
 * teleport them somewhere they never chose to go.
 *
 * The first match in document order wins. A step CAN hold two triggered
 * redirects (two radios, two redirecting answers), but only one navigation can
 * happen, and "the first one you meet reading down the page" is the only order
 * the advisor can predict.
 */
export function findOptionRedirect(
  step: WorkflowStep,
  values: Record<string, FieldValue>,
): OptionRedirect | null {
  if (step.type !== 'DynamicForm') return null;
  for (const section of step.sections) {
    if (
      (section.conditions?.length ?? 0) > 0 &&
      !evaluateConditions(section.conditions, section.conditionCombinator, values)
    ) {
      continue;
    }
    for (const row of section.rows) {
      for (const col of row.columns) {
        const field = col.field;
        if (!field || field.type !== 'RadioGroup') continue;
        const value = values[field.id];
        if (typeof value !== 'string' || value === '') continue;
        const option = field.options?.find((o) => o.value === value);
        if (!option) continue;
        const templateId = option.redirectTemplateId?.trim() || null;
        const url = option.redirectUrl?.trim() || null;
        if (!templateId && !url) continue;
        // Both set is a shape the domain refuses; if one ever reaches the
        // runtime anyway (hand-written DDB row, older data), navigating to a
        // guessed winner is worse than not navigating at all.
        if (templateId && url) continue;
        if (
          (field.conditions?.length ?? 0) > 0 &&
          !evaluateConditions(field.conditions, field.conditionCombinator, values)
        ) {
          continue;
        }
        return { field, option, templateId, url };
      }
    }
  }
  return null;
}

/**
 * The originator-authored modal body, FR falling back to EN — the same
 * convention as hints, required messages and block-continue copy. Null when
 * nothing was authored, which is the caller's cue to use the generic string.
 */
export function redirectMessageFor(option: SelectOption, language: 'en' | 'fr'): string | null {
  const authored =
    language === 'fr' && option.redirectMessageFr?.trim()
      ? option.redirectMessageFr
      : option.redirectMessageEn;
  return authored?.trim() ? authored.trim() : null;
}

/**
 * Carry out a confirmed redirect. Shared by every surface that hosts a
 * {@link FormShell} so they agree on the parts that must not differ, while each
 * still owns the part that must: `internalPathFor` maps a template id to a route
 * on THAT surface (an originator/approver preview, a fresh advisor application).
 *
 * The URL is re-checked here even though the domain already refused anything but
 * http(s) at save time. This is the line that actually hands a string to the
 * browser, and the string may predate that validation or arrive from a
 * hand-written row; a `javascript:` destination reaching this point would
 * execute in the advisor's session. Cheap check, and it belongs where the
 * navigation happens rather than only where the data was written.
 */
export function followOptionRedirect(
  target: { templateId: string | null; url: string | null },
  navigate: (path: string) => void,
  internalPathFor: (templateId: string) => string,
): void {
  if (target.templateId) {
    navigate(internalPathFor(target.templateId));
    return;
  }
  if (!target.url) return;
  let parsed: URL;
  try {
    parsed = new URL(target.url);
  } catch {
    return;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return;
  window.location.assign(parsed.toString());
}

/**
 * Stable identity for one triggered rule — "this field, answered this way".
 *
 * The modal is dismissible, and a dismissal has to stick to the ANSWER rather
 * than to the field: re-picking the same option after cancelling would re-pop a
 * modal the advisor just declined, and picking a DIFFERENT redirecting option
 * has to pop a fresh one.
 */
export function redirectKey(redirect: OptionRedirect): string {
  return `${redirect.field.id}:${redirect.option.value}`;
}
